// src/pages/admin/ManageAdmins.jsx
// Super-admin only — add / remove admin email addresses (the Google-login
// allow-list). The owner (fsgwd01@gmail.com) cannot be removed.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/axios";
import toast from "react-hot-toast";

export default function ManageAdmins() {
  const navigate = useNavigate();
  const requesterEmail = localStorage.getItem("adminEmail") || "";
  const role = localStorage.getItem("adminRole") || "";

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [makeSuper, setMakeSuper] = useState(false);
  const [busy, setBusy] = useState(false);

  // Guard: only super-admins
  useEffect(() => {
    if (role !== "super") {
      toast.error("Super-admin only.");
      navigate("/admin-dashboard", { replace: true });
    }
  }, [role, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin-auth/admins", {
        params: { requesterEmail },
      });
      if (res.data?.success) setAdmins(res.data.admins || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (requesterEmail) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addAdmin = async (e) => {
    e?.preventDefault?.();
    const em = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin-auth/admins", {
        requesterEmail,
        email: em,
        role: makeSuper ? "super" : "admin",
      });
      if (res.data?.success) {
        toast.success("Admin added ✓");
        setEmail("");
        setMakeSuper(false);
        load();
      } else {
        toast.error(res.data?.message || "Failed");
      }
    } catch (e2) {
      toast.error(e2.response?.data?.message || "Failed to add admin");
    } finally {
      setBusy(false);
    }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.email}?`)) return;
    try {
      const res = await api.delete(`/admin-auth/admins/${a._id}`, {
        data: { requesterEmail },
      });
      if (res.data?.success) {
        toast.success("Removed ✓");
        setAdmins((prev) => prev.filter((x) => x._id !== a._id));
      } else {
        toast.error(res.data?.message || "Failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to remove");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin-dashboard")}
              className="h-9 w-9 rounded-full border bg-white font-bold hover:bg-gray-50"
              title="Back"
            >
              ←
            </button>
            <h1 className="text-xl font-extrabold text-gray-900">Manage Admins</h1>
          </div>
        </div>

        {/* Add admin */}
        <form
          onSubmit={addAdmin}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5"
        >
          <p className="text-sm font-bold text-gray-800 mb-2">Add a new admin</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@gmail.com (Google account)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-gray-800 text-white font-bold text-sm hover:bg-gray-900 disabled:opacity-60"
            >
              {busy ? "Adding…" : "Add Admin"}
            </button>
          </div>
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={makeSuper}
              onChange={(e) => setMakeSuper(e.target.checked)}
            />
            Make super-admin (can also manage admins)
          </label>
          <p className="mt-1 text-[11px] text-gray-400">
            The email must be a Google account. They sign in with “Sign in with
            Google”.
          </p>
        </form>

        {/* List */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b text-sm font-bold text-gray-700">
            Admins ({admins.length})
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : admins.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No admins yet.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {admins.map((a) => (
                <li key={a._id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {a.email}
                      {a.role === "super" && (
                        <span className="ml-2 text-[10px] font-bold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                          SUPER
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {a.name ? a.name + " · " : ""}
                      added by {a.addedBy || "—"}
                    </p>
                  </div>
                  <button
                    onClick={() => removeAdmin(a)}
                    disabled={a.email === requesterEmail || a.email === "fsgwd01@gmail.com"}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={
                      a.email === requesterEmail
                        ? "You can't remove yourself"
                        : a.email === "fsgwd01@gmail.com"
                          ? "Owner can't be removed"
                          : "Remove"
                    }
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
